import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PS } from '../theme';
import { BACKEND_URL } from '../lib/api';
import { Avatar, Chip } from '../lib/ui';
import { Icon } from '../lib/icons';
import Screen from '../components/Screen';
import { useFetch } from '../lib/useFetch';
import api from '../lib/api';
import { notifyError } from '../lib/toast';

const INCLUDED = [
  { k: 'สัญญาเล่นแชร์ (e-sig ETDA)', sub: 'พร้อม content hash' },
  { k: 'รายการโอนที่แนบสลิป', sub: 'สลิป + hash + timestamp' },
  { k: 'ประวัติข้อความในแชทกลุ่ม', sub: 'พร้อม timestamp' },
  { k: 'บันทึกผลการเปียทุกงวด', sub: 'winner + ดอกเปีย' },
  { k: 'Audit log (hash chain ทั้งหมด)', sub: 'รวม GPS, device' },
];

export default function Dispute() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: members } = useFetch(`/circles/${id}/members`, [id]);
  const { data: stats } = useFetch(`/circles/${id}/evidence/stats`, [id]);
  const [subject, setSubject] = useState('');
  const [reason, setReason] = useState('ค้างชำระเกินกำหนด');
  const [pkg, setPkg] = useState(null);
  const [busy, setBusy] = useState(false);

  const late = (members || []).filter(m => m.pay_status === 'late');
  const subjectName = subject || late[0]?.name || members?.[1]?.name || '';

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post('/disputes', { circle_id: id, subject_name: subjectName, reason });
      setPkg(res.data);
    } catch { notifyError('เกิดข้อผิดพลาด'); } finally { setBusy(false); }
  };

  const download = () => {
    window.open(`${BACKEND_URL}/api/disputes/${pkg.dispute_id}/download`, '_blank');
  };

  return (
    <Screen bg={PS.page}>
      <div style={{ padding: '14px 20px 14px', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button data-testid="dispute-back-btn" onClick={() => nav(-1)} className="ps-press" style={{ border: 'none', background: 'transparent', padding: 0 }}><Icon.chevronL size={20} stroke={PS.ink} /></button>
          <div>
            <div style={{ fontSize: 10.5, color: PS.textMuted, letterSpacing: 1, textTransform: 'uppercase' }}>ข้อพิพาท · คำร้องใหม่</div>
            <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: -0.3 }}>ขอเอกสารเพื่อดำเนินคดี</div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: PS.textMuted, marginTop: 10, lineHeight: 1.5 }}>
          Peershare <b>ไม่ตัดสิน</b>ว่าใครถูกผิด — เราส่งมอบหลักฐานทั้งหมดให้คุณนำไปใช้กับศาลหรือสถานีตำรวจ
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 110px' }}>
        {/* Subject selector */}
        <div style={{ padding: 16, background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 18 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Avatar name={subjectName || '?'} size={44} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>คู่กรณี</div>
              <select data-testid="dispute-subject" value={subjectName} onChange={e => setSubject(e.target.value)} style={{ width: '100%', height: 38, borderRadius: 10, border: `1px solid ${PS.line}`, padding: '0 10px', fontSize: 13.5, fontWeight: 600, fontFamily: PS.fontUI, background: PS.surface2 }}>
                {(members || []).filter(m => m.role !== 'host').map(m => <option key={m.membership_id} value={m.name}>{m.name} {m.pay_status === 'late' ? '· ค้างชำระ' : ''}</option>)}
              </select>
            </div>
            {late.length > 0 && <Chip tone="warn">ค้าง {late.length} คน</Chip>}
          </div>
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 11, color: PS.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>เหตุผล</div>
            <input data-testid="dispute-reason" value={reason} onChange={e => setReason(e.target.value)} style={{ width: '100%', height: 42, borderRadius: 10, border: `1px solid ${PS.line}`, padding: '0 12px', fontSize: 13.5, fontFamily: PS.fontUI, outline: 'none' }} />
          </div>
        </div>

        <div style={{ padding: '18px 4px 8px', fontSize: 14, fontWeight: 600 }}>หลักฐานที่จะรวมในซอง <span className="num" style={{ color: PS.textMuted, fontWeight: 400 }}>({stats?.event_count ?? '—'} event)</span></div>
        <div style={{ background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 18, overflow: 'hidden' }}>
          {INCLUDED.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '12px 14px', alignItems: 'center', borderBottom: i < INCLUDED.length - 1 ? `1px solid ${PS.lineSoft}` : 'none' }}>
              <div style={{ width: 20, height: 20, borderRadius: 6, background: PS.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.check size={12} stroke={PS.page} sw={2.5} /></div>
              <div style={{ flex: 1 }}><div style={{ fontSize: 12.5, fontWeight: 600 }}>{r.k}</div><div style={{ fontSize: 10.5, color: PS.textMuted }}>{r.sub}</div></div>
            </div>
          ))}
        </div>

        {/* Sealed package preview */}
        {pkg && (
          <div className="ps-fade-up" style={{ marginTop: 18, padding: 20, background: PS.ink, color: PS.page, borderRadius: 18 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
              <Icon.lock size={13} stroke="oklch(0.82 0.1 82)" />
              <div style={{ fontSize: 10.5, letterSpacing: 1.2, textTransform: 'uppercase', color: 'oklch(0.82 0.1 82)', fontWeight: 600 }}>Sealed Evidence Package</div>
            </div>
            <div className="mono" style={{ fontSize: 12, fontWeight: 600 }}>DISPUTE_{pkg.dispute_id}.zip</div>
            <div style={{ display: 'flex', gap: 16, marginTop: 14 }}>
              <div><div style={{ fontSize: 9.5, color: 'rgba(245,241,234,0.55)', textTransform: 'uppercase', letterSpacing: 0.8 }}>ไฟล์</div><div className="mono" style={{ fontSize: 12, marginTop: 3 }}>{pkg.file_count}</div></div>
              <div><div style={{ fontSize: 9.5, color: 'rgba(245,241,234,0.55)', textTransform: 'uppercase', letterSpacing: 0.8 }}>ผู้ออก</div><div className="mono" style={{ fontSize: 12, marginTop: 3 }}>Peershare CA</div></div>
              <div><div style={{ fontSize: 9.5, color: 'rgba(245,241,234,0.55)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Hash</div><div className="mono" style={{ fontSize: 12, marginTop: 3 }}>{pkg.zip_hash_short}</div></div>
            </div>
            <div className="mono" style={{ marginTop: 14, padding: 10, background: 'rgba(245,241,234,0.08)', borderRadius: 8, fontSize: 9.5, color: 'rgba(245,241,234,0.7)', lineHeight: 1.5, wordBreak: 'break-all' }}>
              signed: {pkg.ca_signature.slice(0, 48)}…<br />ed25519 over SHA-256(manifest)
            </div>
            <button data-testid="dispute-verify-link" onClick={() => nav('/verify')} className="ps-press"
              style={{ marginTop: 12, width: '100%', background: 'rgba(245,241,234,0.1)', border: '1px solid rgba(245,241,234,0.18)', color: '#f5f1ea', borderRadius: 10, padding: '10px 12px', fontSize: 12, fontWeight: 600, fontFamily: PS.fontUI, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
              <Icon.shield size={13} stroke="oklch(0.82 0.12 82)" /> ตรวจสอบซองนี้เองที่หน้า “ตรวจสอบซองหลักฐาน”
            </button>
          </div>
        )}

        <div style={{ marginTop: 14, padding: 14, background: 'oklch(0.88 0.06 80 / 0.35)', borderRadius: 12, display: 'flex', gap: 10 }}>
          <Icon.info size={16} stroke="oklch(0.42 0.1 60)" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 11.5, color: 'oklch(0.32 0.1 60)', lineHeight: 1.5 }}>ทั้งสองฝ่ายจะได้รับสำเนาพร้อมกัน และระบบแจ้งเตือนผู้ถูกฟ้องตามนโยบายความโปร่งใส</div>
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${PS.line}`, padding: '12px 16px', background: PS.surface2, display: 'flex', gap: 10, flexShrink: 0 }}>
        {pkg ? (
          <button data-testid="dispute-download-btn" onClick={download} className="ps-press" style={{ flex: 1, height: 48, background: 'oklch(0.52 0.14 28)', color: '#fff', borderRadius: 24, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 700, fontFamily: PS.fontUI }}>
            <Icon.lock size={14} stroke="#fff" sw={2} />ดาวน์โหลดซองปิดผนึก
          </button>
        ) : (
          <button data-testid="dispute-generate-btn" disabled={busy || !subjectName} onClick={generate} className="ps-press" style={{ flex: 1, height: 48, background: PS.ink, color: PS.page, borderRadius: 24, border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 14, fontWeight: 700, fontFamily: PS.fontUI, opacity: busy || !subjectName ? 0.6 : 1 }}>
            {busy ? 'กำลังสร้างซอง…' : 'สร้างซองหลักฐานปิดผนึก'}
          </button>
        )}
      </div>
    </Screen>
  );
}
